import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Package, Users, Paperclip, GripVertical, List, Search, X } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import config from '../config';
const API = `${config.BACKEND_URL}/api`;

const PRIORITIES = [
  { value: 'critical', label: 'Critical', color: 'bg-red-500' },
  { value: 'very-high', label: 'Very High', color: 'bg-orange-500' },
  { value: 'high', label: 'High', color: 'bg-yellow-500' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-500' },
  { value: 'low', label: 'Low', color: 'bg-gray-400' }
];

// Draggable Story Card
const DraggableStoryCard = ({ story, users }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: story.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <StoryCard story={story} users={users} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
};

// Story Card Component
const StoryCard = ({ story, users, dragHandleProps, isOverlay }) => {
  const getPriorityIndicator = (priority) => {
    const p = PRIORITIES.find(pr => pr.value === priority) || PRIORITIES[3];
    return <div className={`w-1 h-full absolute left-0 top-0 rounded-l ${p.color}`} />;
  };

  const getPriorityBadge = (priority) => {
    const p = PRIORITIES.find(pr => pr.value === priority) || PRIORITIES[3];
    return <Badge variant="outline" className="text-xs capitalize">{p.label}</Badge>;
  };

  return (
    <Card className={`relative overflow-hidden ${isOverlay ? 'shadow-lg ring-2 ring-primary' : 'hover:shadow-md'} transition-shadow`}>
      {getPriorityIndicator(story.priority)}
      <CardContent className="p-3 pl-4">
        <div className="flex items-start gap-2 mb-2">
          {dragHandleProps && (
            <div {...dragHandleProps} className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground mt-0.5">
              <GripVertical className="h-4 w-4" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="issue-number text-xs">{story.issue_number}</span>
              {getPriorityBadge(story.priority)}
            </div>
            <Link
              to={`/user-stories/${story.id}`}
              className="font-medium text-sm hover:text-primary line-clamp-2 block"
              onClick={(e) => isOverlay && e.preventDefault()}
            >
              {story.title}
            </Link>
          </div>
        </div>
        
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            {story.release_version && (
              <span className="flex items-center gap-1">
                <Package className="h-3 w-3" />v{story.release_version}
              </span>
            )}
            {story.attachments?.length > 0 && (
              <span className="flex items-center gap-1">
                <Paperclip className="h-3 w-3" />{story.attachments.length}
              </span>
            )}
          </div>
          {story.assigned_users?.length > 0 && (
            <div className="flex items-center gap-1">
              <Users className="h-3 w-3" /><span>{story.assigned_users.length}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Droppable Column
const DroppableColumn = ({ status, stories, users, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: status.key });

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 mb-3 px-1">
        <div className={`w-3 h-3 rounded-full ${status.color}`} />
        <h3 className="font-semibold text-sm">{status.name}</h3>
        <Badge variant="secondary" className="ml-auto">{stories?.length || 0}</Badge>
      </div>
      
      <div
        ref={setNodeRef}
        className={`bg-muted/30 rounded-lg p-2 min-h-[500px] space-y-2 transition-colors ${
          isOver ? 'bg-primary/10 ring-2 ring-primary/50' : ''
        }`}
      >
        {children}
      </div>
    </div>
  );
};

export const Kanban = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [stories, setStories] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeStory, setActiveStory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchKanbanData = useCallback(async () => {
    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      
      const response = await axios.get(`${API}/user-stories/kanban/${projectId}`, { params });
      setProject(response.data.project);
      setStories(response.data.stories);
      setStatuses(response.data.statuses || []);
    } catch (error) {
      console.error('Failed to fetch kanban data:', error);
      toast.error('Failed to load kanban board');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  }, [projectId, debouncedSearch, navigate]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/list`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  useEffect(() => {
    fetchKanbanData();
    fetchUsers();
  }, [fetchKanbanData]);

  const findStoryById = (id) => {
    for (const status of Object.keys(stories)) {
      const story = stories[status]?.find(s => s.id === id);
      if (story) return { story, status };
    }
    return null;
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    const found = findStoryById(active.id);
    if (found) setActiveStory(found.story);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveStory(null);

    if (!over) return;

    const activeData = findStoryById(active.id);
    if (!activeData) return;

    // Determine target status - over.id could be a column key or another story id
    let targetStatus = over.id;
    const statusKeys = statuses.map(s => s.key);
    if (!statusKeys.includes(over.id)) {
      // Dropped on a story, find its status
      const overData = findStoryById(over.id);
      if (overData) targetStatus = overData.status;
      else return;
    }

    // If status changed, update in backend
    if (activeData.status !== targetStatus) {
      // Optimistic update
      setStories(prev => {
        const newStories = { ...prev };
        newStories[activeData.status] = prev[activeData.status].filter(s => s.id !== active.id);
        newStories[targetStatus] = [...prev[targetStatus], { ...activeData.story, status: targetStatus }];
        return newStories;
      });

      try {
        await axios.put(`${API}/user-stories/${active.id}`, { status: targetStatus });
        const statusLabel = statuses.find(s => s.key === targetStatus)?.name || targetStatus;
        toast.success(`Moved to ${statusLabel}`);
      } catch (error) {
        toast.error('Failed to update status');
        fetchKanbanData(); // Revert on error
      }
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setActiveStory(null);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner h-8 w-8" /></div>;
  }

  const totalStories = Object.values(stories).reduce((sum, arr) => sum + arr.length, 0);

  return (
    <div className="space-y-6" data-testid="kanban-board">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/projects')} data-testid="back-to-projects">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="font-mono">{project?.prefix}</Badge>
            <span className="text-muted-foreground">•</span>
            <span className="text-sm text-muted-foreground">{totalStories} stories</span>
          </div>
          <h1 className="text-2xl font-semibold">{project?.name} - Kanban Board</h1>
        </div>
        <Link to={`/user-stories?project=${projectId}`}>
          <Button variant="outline" className="gap-2"><List className="h-4 w-4" />View as List</Button>
        </Link>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search stories by title, issue number, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9"
            data-testid="kanban-search-input"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
              onClick={clearSearch}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground">Drag cards to change status • Sorted by priority</p>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="flex gap-4 overflow-x-auto pb-4">
          {statuses.map((status) => (
            <div key={status.key} className="flex-shrink-0 w-72">
              <DroppableColumn status={status} stories={stories[status.key]} users={users}>
                <SortableContext items={stories[status.key]?.map(s => s.id) || []} strategy={verticalListSortingStrategy}>
                  {stories[status.key]?.length === 0 ? (
                    <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                      {searchQuery ? 'No matching stories' : 'Drop stories here'}
                    </div>
                  ) : (
                    stories[status.key]?.map((story) => (
                      <DraggableStoryCard key={story.id} story={story} users={users} />
                    ))
                  )}
                </SortableContext>
              </DroppableColumn>
            </div>
          ))}
        </div>

        <DragOverlay>
          {activeStory ? <StoryCard story={activeStory} users={users} isOverlay /> : null}
        </DragOverlay>
      </DndContext>

      {/* Legend */}
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center gap-6 flex-wrap">
            <span className="text-sm font-medium">Priority:</span>
            {PRIORITIES.map((p) => (
              <div key={p.value} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded ${p.color}`} />
                <span className="text-sm text-muted-foreground">{p.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
